import { useQuery } from "@tanstack/react-query";
import {
  type Cliente,
  type ClienteEsterno,
  type Vendita,
  type VenditaPOSTRequest,
} from "../models/restAPI";
import { b2bApiEndpoint, publicApiEndpoint } from "../config";
import { userManager } from "./user-manager.service";
const fetchAuth = async (
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
): Promise<Response> => {
  const user = await userManager.getUser();
  return fetch(input, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
      Authorization: `Bearer ${user?.access_token}`,
    },
  });
};

const fetchAuthDelete = async (
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
): Promise<Response> => {
  const user = await userManager.getUser();
  return fetch(input, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${user?.access_token}`,
    },
  });
};

export const fetchJson = async <T = unknown | undefined>(
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
): Promise<T> => {
  const res = await fetchAuth(input, {
    ...init,
  });
  switch (res.status) {
    case 204:
      return undefined as unknown as T;
    case 200:
    case 201:
      return res.json() as Promise<T>;
    case 401:
      throw new Error("Non autenticato");
    case 403:
      throw new Error("Non autorizzato");
    case 404:
      throw new Error((await res.json())?.message || "Risorsa non trovata");
    default: {
      let errorMessage = "Errore durante la richiesta";
      try {
        const json = (await res.json()) as { message?: string };
        if (json.message) {
          errorMessage = json.message;
        }
      } catch (e) {
        // ignore json parse error
      }
      throw new Error(errorMessage);
    }
  }
};

export const deleteResource = async (
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
): Promise<void> => {
  const res = await fetchAuthDelete(input, {
    ...init,
    method: "DELETE",
  });
  switch (res.status) {
    case 204:
    case 200:
    case 201:
      return;
    case 401:
      throw new Error("Non autenticato");
    case 403:
      throw new Error("Non autorizzato");
    case 404:
      throw new Error((await res.json())?.message || "Risorsa non trovata");
    default: {
      let errorMessage = "Errore durante la richiesta";
      try {
        const json = (await res.json()) as { message?: string };
        if (json.message) {
          errorMessage = json.message;
        }
      } catch (e) {
        // ignore json parse error
      }
      throw new Error(errorMessage);
    }
  }
};

export interface UserResponse {
  Kind: number;
  UserName: string;
  UserEmail: string;
  PhoneNumber: PhoneNumber;
  Agent: Agent;
  General: General;
}

export interface Agent {
  Id: string;
  Code: string;
  Name: string;
}

export interface General {
  HasVariants: boolean;
  HasAssortments: boolean;
  HandleMultipleCarts: boolean;
  SharedCartsCount: number;
  CanShareCarts: boolean;
  HasSalesCampaigns: boolean;
  HasAgentAttachments: boolean;
  HasUserAttachments: boolean;
  ShowCustomerDocuments: boolean;
  UserInvitations: UserInvitations;
  Users: Users;
}

export interface UserInvitations {
  CanView: boolean;
  CanRenew: boolean;
  CanResend: boolean;
}

export interface Users {
  CanView: boolean;
}

export interface PhoneNumber {
  Confirmed: boolean;
  CanChange: boolean;
  RequireConfirmation: boolean;
}

export const useUser = () => {
  return useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const user = await fetchJson<UserResponse>(
        `${b2bApiEndpoint}/api/v1/User`,
      ); // Effettua una richiesta autenticata per verificare se l'utente è autenticato
      if (!user) {
        throw new Error("Non autenticato");
      }

      let kindStr = "Sconosciuto";
      switch (user.Kind) {
        case 2:
          kindStr = "Customer";
          break;
        case 4:
          kindStr = "Agent";
          break;
        default:
          throw new Error(`Tipo utente non riconosciuto: ${user.Kind}`);
      }

      return {
        ...user,
        /* Traduco Kind da intero a string */
        Kind: kindStr,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
export interface ClienteResponse {
  id: number;
  idUsalesman: string;
  usalesmanCustomerName: string;
  customerCode: string;
  ragioneSociale: string | null;
  logoUrl: string | null;
  nomeFiliale: string | null;
  indirizzoFiliale: string | null;
  createdAt: Date;
  updatedAt: Date | null;
  authorEmail: string | null;
  authorSub: string | null;
}

export const useClienteEsterno = () => {
  return useQuery({
    queryKey: ["cliente-esterno"],
    queryFn: () =>
      fetchJson<ClienteEsterno>(`${publicApiEndpoint}/cliente/usalesman`),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
/* export const useCliente = () => {
  return useQuery({
    queryKey: ["cliente"],
    queryFn: async () => {
      return (await fetchJson<Cliente>(`${publicApiEndpoint}/cliente`)) || null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}; */

export const updateCliente = async (data: Partial<Cliente>) => {
  return fetchJson<Cliente>(`${publicApiEndpoint}/cliente`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const saveCliente = async (data: Partial<Cliente> & { id?: number }) => {
  return updateCliente(data);
};

export interface KpiVenditeResponse {
  totalSales: number;
  monthlySales: number;
  monthlyIncrease: number;
  weeklySales: number;
  weeklyIncrease: number;
  dailySales: number;
}

export interface BarcodeInfo {
  barcode: string;
  productname: string;
  codicetaglia: string;
  codicearticolo: string | null;
}

export const getBarcodeInfo = async (barcode: string) => {
  return fetchJson<BarcodeInfo[]>(
    `${publicApiEndpoint}/vendite/barcode?barcode=${barcode}`,
  );
};

export const createVendita = async (data: VenditaPOSTRequest) => {
  return fetchJson<Vendita>(`${publicApiEndpoint}/vendite`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updateVendita = async (id: number, data: VenditaPOSTRequest) => {
  return fetchJson<Vendita>(`${publicApiEndpoint}/vendite/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const saveVendita = async (
  data: VenditaPOSTRequest & { id?: number },
) => {
  if (data.id) {
    const { id, ...vendita } = data;
    return updateVendita(id, vendita);
  }
  return createVendita(data);
};

export const deleteVendita = async (id: number) => {
  return deleteResource(`${publicApiEndpoint}/vendite/${id}`);
};

export const uploadImmaginePuntoVendita = async (file: File) => {
  const user = await userManager.getUser();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${publicApiEndpoint}/immagini-punto-vendita`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${user?.access_token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    let errorMessage = "Errore durante il caricamento dell'immagine";
    try {
      const errorResponse = await res.json();
      if (errorResponse && errorResponse.message) {
        errorMessage = errorResponse.message;
      }
    } catch {
      // Usa il messaggio di default se il parsing del JSON fallisce
    }
    throw new Error(errorMessage);
  }
  return res.json();
};

export const updateOrdineImmagini = async (
  items: { id: number; order: number }[],
) => {
  return fetchJson(`${publicApiEndpoint}/immagini-punto-vendita/reorder`, {
    method: "PUT",
    body: JSON.stringify(items),
  });
};

export const deleteImmaginePuntoVendita = async (id: number) => {
  return deleteResource(`${publicApiEndpoint}/immagini-punto-vendita/${id}`);
};

export interface ImmaginePuntoVendita {
  id: number;
  clientiId: number;
  imageName: string;
  order: number;
  createdAt: string;
  updatedAt: string | null;
}

export const getSchedaPuntoVendita = async () => {
  return fetchJson<any>(`${publicApiEndpoint}/scheda-punto-vendita`);
};

export const updateSchedaPuntoVendita = async (data: any) => {
  return fetchJson<any>(`${publicApiEndpoint}/scheda-punto-vendita`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const getOrdiniImages = async (orderIds: string[]) => {
  return fetchJson<{
    images: {
      name: string;
      product_variant_code: string;
    }[];
  }>(`${publicApiEndpoint}/ordini/immagini?orderIds=${orderIds.join(",")}`);
};

export const exportVenditeExcel = async (
  customerId: number,
  from: string | null,
  to: string,
) => {
  const user = await userManager.getUser();
  const params = new URLSearchParams({
    customer_id: customerId.toString(),
    to,
  });
  if (from) {
    params.set("from", from);
  }
  const res = await fetch(
    `${publicApiEndpoint}/vendite/export?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
    },
  );

  if (!res.ok) {
    let errorMessage = "Errore durante l'esportazione delle vendite";
    try {
      const errorResponse = await res.json();
      if (errorResponse && errorResponse.message) {
        errorMessage = errorResponse.message;
      }
    } catch {
      // Usa il messaggio di default se il parsing del JSON fallisce
    }
    throw new Error(errorMessage);
  }
  return res.blob();
};

export const downloadImages = async (imageNames: string[]) => {
  const user = await userManager.getUser();
  const res = await fetch(`${publicApiEndpoint}/images/download`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${user?.access_token}`,
    },
    body: JSON.stringify({ imageNames }),
  });

  if (!res.ok) {
    let errorMessage = "Errore durante il download delle immagini";
    try {
      const errorResponse = await res.json();
      if (errorResponse && errorResponse.message) {
        errorMessage = errorResponse.message;
      }
    } catch {
      // Usa il messaggio di default se il parsing del JSON fallisce
    }
    throw new Error(errorMessage);
  }
  return res.blob();
};
